
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { SYSTEM_NOTIFICATION_TYPES } from "@/constants/notificationTypes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Listens for new system notifications via Supabase Realtime
 * and shows a toast to management users.
 */
export const useSystemNotifications = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isManagement = isAuthenticated && user?.role === "management";
  const shownIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isManagement) return;

    // Listen for new system notifications in realtime only (no page-load toasts)
    const channel = supabase
      .channel('system-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const newNotification = payload.new as any;
          if (
            SYSTEM_NOTIFICATION_TYPES.includes(newNotification.type) &&
            !shownIds.current.has(newNotification.id)
          ) {
            shownIds.current.add(newNotification.id);
            showSystemToast(newNotification);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isManagement]);

  const showSystemToast = (notification: { id: string; message: string; type: string }) => {
    const title = notification.type === 'user_registration'
      ? "Nieuwe registratie-aanvraag"
      : "Systeemmelding";

    toast(title, {
      description: notification.message || "Er is een nieuwe systeemmelding.",
      duration: 10000,
      action: {
        label: "Bekijken",
        onClick: () => navigate("/admin/registraties"),
      },
    });
  };
};
